import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  MessageSquare, Package, Tag, ArrowLeft, Send, X,
  ShoppingBag, Store, Clock, ChevronRight, Loader2,
  Bell, CheckCircle, AlertCircle, User, LogOut
} from "lucide-react";

// ── helpers ────────────────────────────────────────────────────────────────────
const formatName = (userObjOrString) => {
  if (!userObjOrString) return "User";
  let raw = "";
  if (typeof userObjOrString === "object") {
    if (userObjOrString.first_name || userObjOrString.last_name)
      raw = `${userObjOrString.first_name || ""} ${userObjOrString.last_name || ""}`.trim();
    else raw = userObjOrString.name || userObjOrString.username || userObjOrString.email || "";
  } else raw = String(userObjOrString);
  if (!raw || raw === "undefined") return "User";
  const namePart = raw.split("@")[0];
  const parts = namePart.split(/[\s._-]+/).filter(Boolean);
  if (parts.length > 1) {
    const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    return `${first} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  }
  return parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase() : "User";
};

const getInitials = (name) => {
  if (!name || name === "User") return "U";
  const p = name.split(" ");
  return p.length > 1 ? (p[0][0] + p[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });
};

function StatusTick({ isRead, size = 13 }) {
  const c = isRead ? "#3BA3FF" : "#A0A0A0";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 13 13" fill="none">
        <polyline points="2,7 5,10 11,3" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {isRead && (
        <svg width={size} height={size} viewBox="0 0 13 13" fill="none" style={{ marginLeft: -7 }}>
          <polyline points="2,7 5,10 11,3" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

// ── Chat Panel ────────────────────────────────────────────────────────────────
function ChatPanel({ convo, myId, myUsername, apiUrl, authToken, onBack }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!convo?.id) return;
    setMsgs([]); setLoading(true);

    const fetchHistory = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/${convo.id}/history/`, {
          headers: { Authorization: `Token ${authToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMsgs(data.results || data);
        }
      } finally { setLoading(false); }
    };
    fetchHistory();

    const wsBase = apiUrl.startsWith("https://")
      ? apiUrl.replace("https://", "wss://")
      : apiUrl.replace("http://", "ws://");
    const socket = new WebSocket(`${wsBase}/ws/chat/tradelink/${convo.id}/?token=${authToken}`);
    wsRef.current = socket;

    socket.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      setMsgs((prev) => {
        const existIdx = prev.findIndex((m) => m.id === msg.id);
        if (existIdx !== -1) {
          const u = [...prev]; u[existIdx] = { ...u[existIdx], ...msg }; return u;
        }
        const optIdx = prev.findIndex((m) => m.isOptimistic && m.content === msg.content);
        if (optIdx !== -1) { const u = [...prev]; u[optIdx] = msg; return u; }
        return [...prev, msg];
      });
    };
    return () => { if (socket.readyState === WebSocket.OPEN) socket.close(); };
  }, [convo?.id, apiUrl, authToken]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!input.trim() || !convo?.id) return;
    const text = input.trim(); setInput("");
    const temp = { id: `t-${Date.now()}`, sender: myId, sender_name: myUsername, content: text, is_read: false, isOptimistic: true, created_at: new Date().toISOString() };
    setMsgs((p) => [...p, temp]);
    try {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ message: text }));
      } else {
        await fetch(`${apiUrl}/api/tradelink/chat/send/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Token ${authToken}` },
          body: JSON.stringify({ conversation_id: convo.id, text }),
        });
      }
    } catch {}
  };

  const partnerName = formatName({
    first_name: convo?.partner_first_name,
    last_name: convo?.partner_last_name,
    name: convo?.partner_name,
    email: convo?.partner_email,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #e2e8f0", background: "#fff", flexShrink: 0 }}>
        {onBack && (
          <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", display: "flex", padding: 4 }}>
            <ArrowLeft size={18} />
          </button>
        )}
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#e0f2fe", color: "#0284c7", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
          {getInitials(partnerName)}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{partnerName}</div>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{convo?.listing_title}</div>
        </div>
        <div style={{ marginLeft: "auto", fontWeight: 800, fontSize: 15, color: "#f59e0b" }}>
          {convo?.listing_price ? `$${Number(convo.listing_price).toLocaleString()}` : ""}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", background: "#f8fafc", display: "flex", flexDirection: "column", gap: 10 }}>
        {loading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
            <Loader2 size={20} style={{ animation: "spin 1s linear infinite", marginRight: 8 }} /> Loading…
          </div>
        ) : msgs.length === 0 ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8", gap: 8 }}>
            <MessageSquare size={32} style={{ opacity: 0.4 }} />
            <div style={{ fontWeight: 600, fontSize: 13 }}>No messages yet</div>
            <div style={{ fontSize: 12 }}>Start the conversation below</div>
          </div>
        ) : (
          msgs.map((m, i) => {
            const isMe = m.isOptimistic ||
              (myId && (String(m.sender) === String(myId) || String(m.sender_id) === String(myId))) ||
              (myUsername && m.sender_name && String(m.sender_name).toLowerCase() === String(myUsername).toLowerCase());
            return (
              <div key={m.id || i} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", maxWidth: "75%", alignItems: isMe ? "flex-end" : "flex-start" }}>
                  <div style={{ padding: "10px 14px", fontSize: 14, borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px", background: isMe ? "#0891b2" : "#fff", color: isMe ? "#fff" : "#0f172a", border: isMe ? "none" : "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                    {m.content}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3, padding: "0 4px" }}>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    {isMe && <StatusTick isRead={m.is_read} size={11} />}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "12px 16px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 10, background: "#fff", borderRadius: 24, border: "1px solid #e2e8f0", padding: "6px 6px 6px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <input
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Write a message…"
            style={{ flex: 1, border: "none", outline: "none", fontSize: 14, color: "#0f172a", background: "transparent" }}
          />
          <button onClick={send} disabled={!input.trim()} style={{ width: 36, height: 36, borderRadius: "50%", background: input.trim() ? "#0891b2" : "#e2e8f0", border: "none", cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background .15s" }}>
            <Send size={15} color={input.trim() ? "#fff" : "#94a3b8"} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Conversation Row ──────────────────────────────────────────────────────────
function ConvoRow({ convo, isActive, onClick, myId, myUsername }) {
  const partnerName = formatName({
    first_name: convo.partner_first_name, last_name: convo.partner_last_name,
    name: convo.partner_name, email: convo.partner_email,
  });

  return (
    <div onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer",
      background: isActive ? "#f0f9ff" : "#fff",
      borderLeft: `3px solid ${isActive ? "#0891b2" : "transparent"}`,
      borderBottom: "1px solid #f1f5f9", transition: "all .12s",
    }}
      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f8fafc"; }}
      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "#fff"; }}
    >
      <div style={{ width: 42, height: 42, borderRadius: "50%", background: isActive ? "#e0f2fe" : "#f1f5f9", color: isActive ? "#0284c7" : "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
        {getInitials(partnerName)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{partnerName}</div>
          {convo.last_message_time && <div style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>{timeAgo(convo.last_message_time)}</div>}
        </div>
        <div style={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>{convo.listing_title}</div>
        {convo.last_message && (
          <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{convo.last_message}</div>
        )}
      </div>
      {convo.unread_count > 0 && (
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#0891b2", color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {convo.unread_count}
        </div>
      )}
    </div>
  );
}

// ── My Listing Row (Selling tab) ──────────────────────────────────────────────
function ListingRow({ listing, inquiries, onSelectConvo, activeConvoId }) {
  const [open, setOpen] = useState(false);
  const hasInquiries = inquiries && inquiries.length > 0;

  return (
    <div style={{ borderBottom: "1px solid #f1f5f9" }}>
      <div onClick={() => hasInquiries && setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: hasInquiries ? "pointer" : "default", background: "#fff", transition: "background .12s" }}
        onMouseEnter={(e) => { if (hasInquiries) e.currentTarget.style.background = "#f8fafc"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
      >
        {/* Thumb */}
        <div style={{ width: 52, height: 44, borderRadius: 8, background: "#f1f5f9", overflow: "hidden", flexShrink: 0 }}>
          {listing.images?.length > 0
            ? <img src={listing.images[0].image} alt={listing.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📦</div>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{listing.title}</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#f59e0b" }}>${Number(listing.price).toLocaleString()}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {hasInquiries ? (
            <div style={{ padding: "3px 10px", borderRadius: 20, background: "#0891b2", color: "#fff", fontSize: 11, fontWeight: 700 }}>
              {inquiries.length} {inquiries.length === 1 ? "inquiry" : "inquiries"}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#94a3b8" }}>No inquiries yet</div>
          )}
          {hasInquiries && <ChevronRight size={14} color="#94a3b8" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }} />}
        </div>
      </div>
      {/* Inquiries sub-list */}
      {open && hasInquiries && (
        <div style={{ background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
          {inquiries.map((inq) => {
            const buyerName = formatName({ first_name: inq.partner_first_name, last_name: inq.partner_last_name, name: inq.partner_name, email: inq.partner_email });
            return (
              <div key={inq.id} onClick={() => onSelectConvo(inq)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px 11px 28px", cursor: "pointer", borderLeft: `3px solid ${activeConvoId === inq.id ? "#0891b2" : "transparent"}`, background: activeConvoId === inq.id ? "#f0f9ff" : "transparent", transition: "all .12s" }}
                onMouseEnter={(e) => { if (activeConvoId !== inq.id) e.currentTarget.style.background = "#f0f9ff"; }}
                onMouseLeave={(e) => { if (activeConvoId !== inq.id) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e0f2fe", color: "#0284c7", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12, flexShrink: 0 }}>{getInitials(buyerName)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#0f172a" }}>{buyerName}</div>
                  {inq.last_message && <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inq.last_message}</div>}
                </div>
                {inq.unread_count > 0 && (
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#0891b2", color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{inq.unread_count}</div>
                )}
                {inq.last_message_time && <div style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>{timeAgo(inq.last_message_time)}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────────────────────────
export default function TradeLinkDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("buying"); // "buying" | "selling"
  const [conversations, setConversations] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState("list"); // "list" | "chat"

  const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const authToken = localStorage.getItem("token");

  // Parse user from localStorage
  const userStr = localStorage.getItem("user");
  let user = {};
  try { if (userStr) user = JSON.parse(userStr); } catch {}
  const myId = user?.id || user?.pk;
  const myUsername = user?.username || user?.name || user?.email || localStorage.getItem("username");
  const myName = formatName({ first_name: user?.first_name, last_name: user?.last_name, name: user?.name || user?.username, email: user?.email });
  const myRole = user?.role || user?.user_type || (user?.is_company ? "company" : user?.is_dispatcher ? "dispatcher" : "driver");

  // Role badge colors
  const roleMeta = {
    driver:     { label: "Pilot Driver",  color: "#0891b2", bg: "#e0f2fe" },
    dispatcher: { label: "Dispatcher",    color: "#7c3aed", bg: "#ede9fe" },
    company:    { label: "Company",       color: "#059669", bg: "#d1fae5" },
  };
  const role = roleMeta[myRole] || { label: "Member", color: "#64748b", bg: "#f1f5f9" };

  // Redirect if not logged in
  useEffect(() => {
    if (!authToken) { navigate("/login"); return; }
  }, [authToken, navigate]);

  // Fetch inbox + my listings
  useEffect(() => {
    if (!authToken) return;
    const load = async () => {
      setLoading(true);
      try {
        const [inboxRes, listingsRes] = await Promise.all([
          fetch(`${apiUrl}/api/tradelink/chat/inbox/`, { headers: { Authorization: `Token ${authToken}` } }),
          fetch(`${apiUrl}/api/tradelink/listings/?my=true`, { headers: { Authorization: `Token ${authToken}` } }),
        ]);
        if (inboxRes.ok) {
          const d = await inboxRes.json();
          setConversations(Array.isArray(d) ? d : (d.results || []));
        }
        if (listingsRes.ok) {
          const d = await listingsRes.json();
          setMyListings(Array.isArray(d) ? d : (d.results || []));
        }
      } finally { setLoading(false); }
    };
    load();
  }, [apiUrl, authToken]);

  // Separate buying vs selling conversations
  // "buying" = I am NOT the seller (partner is the seller)
  // "selling" = conversations about my own listings
  const buyingConvos = conversations.filter((c) => {
    // If the listing has a seller_id and it matches my id, it's selling; otherwise buying
    if (c.is_seller !== undefined) return !c.is_seller;
    // Fallback: check if any of my listings matches this conversation's listing title
    const isMyListing = myListings.some((l) => l.title === c.listing_title || String(l.id) === String(c.listing_id));
    return !isMyListing;
  });

  // Group selling convos by listing
  const sellingConvos = conversations.filter((c) => {
    if (c.is_seller !== undefined) return c.is_seller;
    return myListings.some((l) => l.title === c.listing_title || String(l.id) === String(c.listing_id));
  });

  const listingsWithInquiries = myListings.map((l) => ({
    ...l,
    inquiries: sellingConvos.filter((c) => c.listing_title === l.title || String(c.listing_id) === String(l.id)),
  }));

  const handleSelectConvo = (convo) => {
    setActiveConvo(convo);
    setMobileView("chat");
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', sans-serif" }}>
      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <div style={{ background: "#0d1f3c", borderBottom: "3px solid #f59e0b", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, height: 60, position: "sticky", top: 0, zIndex: 100 }}>
        <Link to="/marketplace" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <div style={{ fontSize: 20 }}>🛒</div>
          <span style={{ color: "#fff", fontWeight: 900, fontSize: 16 }}>TradeLink</span>
        </Link>
        <div style={{ height: 24, width: 1, background: "rgba(255,255,255,0.15)" }} />
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, fontWeight: 600 }}>My Dashboard</span>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          {/* Notification dot */}
          {totalUnread > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: "rgba(8,145,178,0.25)", border: "1px solid rgba(8,145,178,0.5)" }}>
              <Bell size={13} color="#38bdf8" />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#38bdf8" }}>{totalUnread} unread</span>
            </div>
          )}
          {/* User chip */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: role.bg, color: role.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12, border: `2px solid ${role.color}40` }}>
              {getInitials(myName)}
            </div>
            <div style={{ display: "none", flexDirection: "column" }} className="md:flex">
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{myName}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: role.color, textTransform: "uppercase", letterSpacing: ".05em" }}>{role.label}</span>
            </div>
          </div>
          <button onClick={() => navigate("/marketplace")} style={{ color: "rgba(255,255,255,0.5)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600 }}>
            Browse <ChevronRight size={13} />
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px", display: "flex", gap: 20, alignItems: "flex-start", minHeight: "calc(100vh - 60px)" }}>

        {/* ── Left: list panel ─────────────────────────────────────────────── */}
        <div style={{ width: 340, flexShrink: 0, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", display: mobileView === "chat" ? "none" : "flex", flexDirection: "column" }}>

          {/* Profile strip */}
          <div style={{ padding: "20px 20px 0", background: "linear-gradient(135deg, #0d1f3c, #1a3358)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: role.bg, color: role.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 18, border: `2px solid ${role.color}60` }}>
                {getInitials(myName)}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>{myName}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: role.color, textTransform: "uppercase", letterSpacing: ".06em", background: role.bg, padding: "2px 8px", borderRadius: 20 }}>{role.label}</span>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>✓ Verified</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, background: "rgba(255,255,255,0.1)", borderRadius: "10px 10px 0 0", overflow: "hidden" }}>
              {[
                ["Listings", myListings.length],
                ["Buying", buyingConvos.length],
                ["Selling", sellingConvos.length],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ padding: "10px 0", textAlign: "center", background: "rgba(255,255,255,0.05)" }}>
                  <div style={{ fontWeight: 900, fontSize: 18, color: "#f59e0b" }}>{val}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
            {[
              { id: "buying", label: "Buying", icon: ShoppingBag, count: buyingConvos.filter(c=>c.unread_count>0).length },
              { id: "selling", label: "Selling", icon: Store, count: sellingConvos.filter(c=>c.unread_count>0).length },
            ].map(({ id, label, icon: Icon, count }) => (
              <button key={id} onClick={() => { setTab(id); setActiveConvo(null); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "12px 8px", background: "none", border: "none", borderBottom: `2px solid ${tab === id ? "#0891b2" : "transparent"}`, cursor: "pointer", fontWeight: tab === id ? 800 : 600, fontSize: 13, color: tab === id ? "#0891b2" : "#64748b", transition: "all .15s", position: "relative" }}>
                <Icon size={14} />
                {label}
                {count > 0 && <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#ef4444", color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{count}</span>}
              </button>
            ))}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 340px)" }}>
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40, color: "#94a3b8", gap: 8 }}>
                <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading…
              </div>
            ) : tab === "buying" ? (
              buyingConvos.length === 0 ? (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8" }}>
                  <ShoppingBag size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>No active inquiries</div>
                  <Link to="/marketplace" style={{ fontSize: 12, color: "#0891b2", fontWeight: 700, textDecoration: "none" }}>Browse listings →</Link>
                </div>
              ) : (
                buyingConvos.map((c) => <ConvoRow key={c.id} convo={c} isActive={activeConvo?.id === c.id} onClick={() => handleSelectConvo(c)} myId={myId} myUsername={myUsername} />)
              )
            ) : (
              <>
                <div style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em" }}>Your Listings</span>
                  <Link to="/tradelink/post-listing" style={{ fontSize: 11, fontWeight: 800, color: "#0891b2", textDecoration: "none" }}>+ Post New</Link>
                </div>
                {listingsWithInquiries.length === 0 ? (
                  <div style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8" }}>
                    <Package size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>No listings yet</div>
                    <Link to="/tradelink/post-listing" style={{ fontSize: 12, color: "#0891b2", fontWeight: 700, textDecoration: "none" }}>Post your first listing →</Link>
                  </div>
                ) : (
                  listingsWithInquiries.map((l) => (
                    <ListingRow key={l.id} listing={l} inquiries={l.inquiries} onSelectConvo={handleSelectConvo} activeConvoId={activeConvo?.id} />
                  ))
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Right: chat panel ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", height: "calc(100vh - 110px)", display: "flex", flexDirection: "column" }}>
          {activeConvo ? (
            <ChatPanel
              convo={activeConvo}
              myId={myId}
              myUsername={myUsername}
              apiUrl={apiUrl}
              authToken={authToken}
              onBack={isMobile ? () => setMobileView("list") : null}
            />
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8", gap: 16, padding: 40 }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>💬</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, color: "#475569", textAlign: "center", marginBottom: 6 }}>
                  {tab === "buying" ? "Select a conversation" : "Select an inquiry"}
                </div>
                <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", maxWidth: 280 }}>
                  {tab === "buying"
                    ? "Pick a listing conversation from the left to continue chatting with the seller."
                    : "Choose an inquiry from one of your listings to respond to a buyer."}
                </div>
              </div>
              {/* Quick links */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", marginTop: 8 }}>
                <Link to="/marketplace" style={{ padding: "8px 16px", borderRadius: 8, background: "#0891b2", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Browse Listings</Link>
                <Link to="/tradelink/post-listing" style={{ padding: "8px 16px", borderRadius: 8, background: "#f1f5f9", color: "#475569", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Post a Listing</Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard link to role-specific dashboard */}
      <div style={{ maxWidth: 1200, margin: "0 auto 32px", padding: "0 16px" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { role: "driver",     label: "Driver Dashboard",     path: "/user-dashboard",    icon: "🚗" },
            { role: "company",    label: "Company Dashboard",    path: "/company-dashboard",  icon: "🏢" },
            { role: "dispatcher", label: "Dispatcher Dashboard", path: "/dispatcher-dashboard", icon: "📡" },
          ].map((item) => (
            <Link key={item.role} to={item.path} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", color: "#475569", fontWeight: 700, fontSize: 13, textDecoration: "none", transition: "all .15s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0891b2"; e.currentTarget.style.color = "#0891b2"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; }}
            >
              <span>{item.icon}</span>{item.label} →
            </Link>
          ))}
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
